
// BattleResultScreen.tsx
import React, { useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { LeatherBox, PergamentBox } from '@/view/ui/common/NineSliceBox';
import { useBattleStore } from '@/store/useBattleStore';
import { useGlobalStore } from '@/store/useGlobalStore';
import { BattleStatus, FactionType } from '@/core/types/battle.types';
import { FactionSide, GameScene } from '@/core/types/global.types';
import { pauseToggle } from '@/core/utils/battle.utils';
import './BattleResultScreen.css';
import { defineMessage } from '@lingui/core/macro';

const TILE = 96;
const WINDOW = 48;
const SCALE = WINDOW / TILE;
const SHEET_W = 4 * TILE * SCALE;
const SHEET_H = 2 * TILE * SCALE;

const DEAD_RATIO_VICTORY = 0.3;
const DEAD_RATIO_DEFEAT = 0.6;

const VICTORY_SUBTITLES = [
    defineMessage({ message: 'The enemy breaks and flees' }),
    defineMessage({ message: 'The battlefield is yours' }),
    defineMessage({ message: 'The enemy host is scattered' }),
    defineMessage({ message: 'The enemy line collapses' }),
    defineMessage({ message: 'The enemy is routed' }),
];

const DEFEAT_SUBTITLES = [
    defineMessage({ message: 'Your army breaks' }),
    defineMessage({ message: 'Your men scatter' }),
    defineMessage({ message: 'The enemy holds the field' }),
    defineMessage({ message: 'The battle is lost' }),
    defineMessage({ message: 'Your forces retreat' }),
];

function calcCasualties(initialCount: number, finalCount: number, isVictory: boolean) {
    const lost = Math.max(0, initialCount - finalCount);
    const deathChance = isVictory ? DEAD_RATIO_VICTORY : DEAD_RATIO_DEFEAT;
    
    let dead = 0;

    for (let i = 0; i < lost; i++) {
        if (Math.random() < deathChance) {
            dead++;
        }
    }

    const wounded = lost - dead;

    return { 
        dead, 
        wounded, 
        surviving: finalCount, 
        returning: finalCount + wounded 
    };
}

function UnitPortraitSmall({ col, row, mirror }: { col: number; row: number, mirror: boolean }) {
    return (
        <div className="unit-portrait-small" style={{
            width: WINDOW,
            height: WINDOW,
            backgroundSize: `${SHEET_W}px ${SHEET_H}px`,
            backgroundPosition: `-${(col * WINDOW) + 1}px -${(row * WINDOW) + 1}px`,
            transform: mirror ? 'scaleX(-1)' : 'none',
        }} />
    );
}

function UnitRow({
    unitName,
    portrait,
    initialCount,
    finalCount,
    isVictory,
    mirror = false,
}: {
    unitName: string;
    portrait: { col: number; row: number } | null;
    initialCount: number;
    finalCount: number;
    isVictory: boolean;
    mirror?: boolean;
}) {
    const { t, i18n } = useLingui();
    const { dead, wounded, returning } = calcCasualties(initialCount, finalCount, isVictory);

    const icon = portrait ? (
        <UnitPortraitSmall col={portrait.col} row={portrait.row} mirror={mirror}/>
    ) : (
        <div style={{ width: WINDOW, height: WINDOW }} className="border border-amber-900/30 flex items-center justify-center text-dark-muted text-xs">
            {unitName.substring(0, 2)}
        </div>
    );

    return (
        <div className={`flex flex-col gap-0 py-2 border-b border-amber-900/20 last:border-0 ${mirror ? 'items-end' : 'items-start'}`}>
            <span className="text-gold-on-paper text-sm font-bold uppercase tracking-wider font-kyiv">
                {i18n._(unitName)}
            </span>
            <div className={`flex items-center gap-3 final-stats ${mirror ? 'flex-row-reverse' : 'flex-row'}`}>
                {icon}
                <div className={`flex flex-col gap-0.5 ${mirror ? 'items-end' : 'items-start'}`}>
                    <span className="text-danger-on-paper text-sm">{t`Killed`}: {dead}</span>
                    <span className="text-gold-on-paper text-sm">{t`Wounded`}: {wounded}</span>
                    <span className="text-success-on-paper text-sm font-medium">{t`Remaining`}: {returning}</span>
                </div>
            </div>
        </div>
    );
}

export const BattleResultScreen = () => {
    const status = useBattleStore(s => s.status);
    const units = useBattleStore(s => s.units);
    
    const initialUnits = useGlobalStore(s => s.mapConfig);
    const globalStore = useGlobalStore();
    const playerSide = useGlobalStore(s => s.playerSide);

    const isPlayerRuthenia = playerSide === FactionSide.RUTHENIA;

    if (status !== BattleStatus.Victory && status !== BattleStatus.Defeat) return null;

    const { i18n } = useLingui();

    const isVictory = status === BattleStatus.Victory;

    const subtitle = useMemo(() => {
        const list = isVictory ? VICTORY_SUBTITLES : DEFEAT_SUBTITLES;
        return list[Math.floor(Math.random() * list.length)]!;
    }, [isVictory]);

    const playerUnits = [...units.values()].filter(u => u.faction === FactionType.Player);
    const enemyUnits = [...units.values()].filter(u => u.faction === FactionType.Enemy);

    const mySide = {
        // units: isPlayerRuthenia ? playerUnits : enemyUnits,
        initialUnitsConfig: isPlayerRuthenia ? initialUnits.playerUnits : initialUnits.enemyUnits,
        label: isPlayerRuthenia ? <Trans>Ruthenian Druzhyna</Trans> : <Trans>Cuman Horde</Trans>,
    };

    const enemySide = {
        // units: isPlayerRuthenia ? enemyUnits : playerUnits,
        initialUnitsConfig: isPlayerRuthenia ? initialUnits.enemyUnits : initialUnits.playerUnits,
        label: isPlayerRuthenia ? <Trans>Cuman Horde</Trans> : <Trans>Ruthenian Druzhyna</Trans>,
    };

    

    return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
            <PergamentBox width={650} height={"90dvh"} padding={40}>
                <div className="flex flex-col gap-6">

                    <div className="text-center">
                        <h1 className={isVictory ? 'victory-title' : 'defeat-title'}>
                            {isVictory ? <Trans>Victory</Trans> : <Trans>Defeat</Trans>}
                        </h1>
                        <p className="text-dark-muted text-base italic font-kyiv mt-4 ">"{i18n._(subtitle)}"</p>
                    </div>

                    <div className="flex justify-center gap-4">
                        <button className="bb-btn bb-btn-no-shadow" onClick={() => globalStore.changeScene(GameScene.BATTLE)}>
                            <Trans>Restart Battle</Trans>
                        </button>
                        <button className="bb-btn bb-btn-no-shadow" onClick={() => {
                            globalStore.setScene(GameScene.WORLD);
                            // globalStore.changeScene(GameScene.WORLD);
                        }}>
                            <Trans>Main Menu</Trans>
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-col">
                            <div className="text-sm font-medium font-kyiv uppercase tracking-wide text-dark-secondary mb-2 border-b border-amber-900/30 pb-1">
                                {mySide.label}
                            </div>
                            {playerUnits.map(unit => (
                                <UnitRow
                                    key={unit.unitId}
                                    unitName={unit.unitName}
                                    portrait={unit.unitPortrait ?? null}
                                    initialCount={mySide.initialUnitsConfig.find(u => u.id === unit.unitId)?.count ?? unit.count}
                                    finalCount={unit.count}
                                    isVictory={isVictory}
                                    mirror={false}
                                />
                            ))}
                        </div>
                        <div className="flex flex-col">
                            <div className="text-sm font-medium font-kyiv uppercase tracking-wide text-dark-secondary mb-2 border-b border-amber-900/30 pb-1 text-right">
                                {enemySide.label}
                            </div>
                            {enemyUnits.map(unit => (
                                <UnitRow
                                    key={unit.unitId}
                                    unitName={unit.unitName}
                                    portrait={unit.unitPortrait ?? null}
                                    initialCount={enemySide.initialUnitsConfig.find(u => u.id === unit.unitId)?.count ?? unit.count}
                                    finalCount={unit.count}
                                    isVictory={!isVictory}
                                    mirror={true}
                                />
                            ))}
                        </div>
                    </div>

                </div>
            </PergamentBox>
        </div>
    );
};